<template>
    <div class="main-container">
        <v-container fluid class="search-panel overflow-auto pa-5">
            <h4 class="mb-2">{{ $t('sub_event_search_title') }}</h4>
            <p style="min-height: 50px">{{ $t('sub_event_search_info') }}</p>
            <!-- search list -->
            <BaseRemoteList
                ref="searchList"
                class="mt-2"
                dense
                color="var(--tgk-grey)"
                session-key="event-sub-events-search-list"
                :collection-class="EventCollection"
                :extra-params="searchQueryParams"
                :filters="searchFilters"
                :headers="searchHeaders"
                :options="{ sortBy: ['next_schedule_date', 'title'], sortDesc: [true, false] }"
                hide-default-footer
                hide-top-toolbar
                :new-btn="false"
                :reload-btn="false"
                disable-pagination
                @loading="loadingSearch = $event"
                @edit-item="onEdit"
            >
                <template #item.status="{ item }">
                    <KpIcon
                        v-if="item.status"
                        :tooltip="$t(item.status)"
                        :icon-cls="`${item.status_icon}`"
                        :color="`${item.status_icon_color}`"
                    />
                </template>
                <template #item.next_schedule_date="{ item }">
                    <span>{{ item.next_schedule_date_display }}</span>
                </template>
                <template #[`item.addSubEventButton`]="{ item }">
                    <ActionBtn :tooltip="$t('assign')" :loading="loadingSubEvents" @click="addSubEvent(item)">
                        <KpIcon icon-cls="arrow-bigright" color="white" />
                    </ActionBtn>
                </template>

                <template #after-query-field>
                    <div
                        :class="{
                            'ml-1': true,
                            'erw-suche-indicator': Object.keys(extendedSearchParams || {}).length > 0,
                        }"
                    >
                        <IconBtn
                            color="grey"
                            :disabled="loadingSearch"
                            icon-cls="advancedsearch"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('erw_suche')"
                            @click="showErwSuche = true"
                        />
                    </div>
                </template>
            </BaseRemoteList>
        </v-container>
        <div class="assigned-panel overflow-auto pa-5">
            <h4 class="mb-2">{{ $t('sub_event_assigned_title') }}</h4>
            <p style="min-height: 50px">{{ $t('sub_event_assigned_info') }}</p>
            <!-- assigned list -->
            <BaseList
                class="mt-2"
                dense
                color="var(--tgk-grey)"
                session-key="event-sub-events-assigned-list"
                :loading="loadingSubEvents"
                :items="subEventCollection.models"
                :headers="assignedHeaders"
                hide-default-footer
                hide-top-toolbar
                :reload-btn="false"
                :new-btn="false"
                :query-field="false"
                :confirm-dialog-title="$t('delete_link')"
                :confirm-dialog-message="$t('delete_link_msg')"
                disable-pagination
                @edit-item="onEdit"
                @delete-item="removeSubEvent"
                @reload="loadSubEvents()"
            >
                <template #item.status="{ item }">
                    <KpIcon
                        v-if="item.status"
                        :tooltip="$t(item.status)"
                        :icon-cls="`${item.status_icon}`"
                        :color="`${item.status_icon_color}`"
                    />
                </template>
                <template #item.next_schedule_date="{ item }">
                    <span>{{ item.next_schedule_date_display }}</span>
                </template>
                <template #toolbar-start>
                    <v-menu offset-y>
                        <template #activator="{ on, attrs }">
                            <v-btn color="action white--text" depressed v-bind="attrs" v-on="on">
                                <KpIcon icon-cls="new" color="white" disabled-color="grey" left />
                                {{ $t('neu_event') }}
                                <KpIcon icon-cls="arrowdown" color="white" disabled-color="grey" right />
                            </v-btn>
                        </template>
                        <EventNewMenu
                            :emit-events="true"
                            @event-express-edit="createNewExpressSubEvent"
                            @event-edit="createNewSubEvent"
                        ></EventNewMenu>
                    </v-menu>
                </template>
            </BaseList>
        </div>
        <!-- enhanced search btn -->
        <v-navigation-drawer v-model="showErwSuche" absolute bottom temporary width="530px" color="kp-light-panel-bg">
            <v-toolbar dense class="kp-sublime-toolbar kp-root-panel-bg" elevation="0">
                <v-toolbar-title>{{ $t('erw_suche') }}</v-toolbar-title>
                <v-spacer />
                <IconBtn icon-cls="x" icon-color="grey" @click="showErwSuche = false" />
            </v-toolbar>
            <EventEnhancedSearch
                ref="search"
                style="grid-area: search; overflow: auto"
                :initial-params="extendedSearchParams"
                @search="onEnhancedSearch"
                @reset="resetEnhancedSearch"
            />
        </v-navigation-drawer>
    </div>
</template>

<script>
import EventEnhancedSearch from '@/components/event/EnhancedSearch.vue';
import sessionStorageMixin from '@/mixins/session-storage';
import { errToStr } from '@/helpers/tools';
import { Event, EventCollection } from '@/models/Event';
import { instance as api } from '@/helpers/api';
import { userIsStaff } from '@/helpers/auth';
import ActionBtn from '@/components/buttons/ActionBtn.vue';
import EventNewMenu from '@/components/event/NewMenu.vue';
import { getRouteParams } from '@/helpers/eventsOpenMode';

export default {
    name: 'EventEditSubEvents',
    components: {
        EventEnhancedSearch,
        ActionBtn,
        EventNewMenu,
    },
    mixins: [sessionStorageMixin],

    props: {
        record: {
            type: Event,
            required: true,
        },
    },

    data() {
        const subEventCollection = new EventCollection();

        return {
            EventCollection,
            loadingSubEvents: false,
            loadingSearch: false,
            showErwSuche: false,
            extendedSearchParams: this.getSessionValue('extendedSearchParams') || {},
            subEventCollection,
            assignedItemToDelete: null,
            searchHeaders: [
                { value: 'status' },
                { text: this.$t('titel'), sortable: true, value: '$.title' },
                { text: this.$t('event_organizer'), sortable: true, value: '$.event_organizer', width: 180 },
                { text: this.$t('next_date'), sortable: true, value: 'next_schedule_date', width: 140 },
                { text: this.$t('assign'), sortable: false, value: 'addSubEventButton', width: 70 },
            ],
            assignedHeaders: [
                { value: 'status' },
                { text: this.$t('titel'), sortable: true, value: '$.title' },
                { text: this.$t('event_organizer'), sortable: true, value: '$.event_organizer', width: 180 },
                {
                    text: this.$t('next_date'),
                    sortable: true,
                    value: 'next_schedule_date',
                    width: 140,
                },
                { sortable: false, value: 'actions', width: 70 },
            ],
        };
    },

    computed: {
        searchQueryParams() {
            return {
                only_next: true,
                exclude_sub_events_for_id: this.record?.id,
                enhancedFilters: this.extendedSearchParams,
            };
        },
        searchFilters() {
            return [
                {
                    property: 'id',
                    operator: 'NEQ', // NEQ = <> but wont be encoded
                    value: this.record?.id,
                },
            ];
        },
    },

    beforeCreate() {
        this.sessionKey = 'event-sub-events';
    },

    async mounted() {
        this.$emit('input', true);
        this.loadSubEvents();
    },

    methods: {
        userIsStaff,

        async loadSubEvents() {
            this.loadingSubEvents = true;
            this.subEventCollection.addExtraParam('btn_all', true);
            this.subEventCollection.addExtraParam('parent_event_id', this.record ? this.record?.getId() : -1);
            await this.subEventCollection.query();
            this.loadingSubEvents = false;
        },

        onEnhancedSearch(params) {
            this.extendedSearchParams = { ...params };
            this.showErwSuche = false;
        },

        resetEnhancedSearch() {
            this.extendedSearchParams = {};
            this.showErwSuche = false;
        },

        onEdit(subEvent) {
            const { name, id } = getRouteParams(subEvent);

            this.$router.push({
                name,
                params: { id, backRoute: { route: this.$route } },
            });
        },

        async addSubEvent(event) {
            if (event) {
                try {
                    this.loadingSubEvents = true;
                    await api().post('/SubEvent/post', {
                        parent_event_id: this.record?.getId(),
                        child_event_id: event?.getId(),
                    });
                    await this.loadSubEvents();
                    this.$refs.searchList.reload();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.loadingSubEvents = false;
                }
            }
        },

        async removeSubEvent(event) {
            if (event) {
                try {
                    this.loadingSubEvents = true;
                    await api().post('/SubEvent/removeLink', {
                        parent_id: this.record?.id,
                        child_id: event?.getId(),
                    });
                    await this.loadSubEvents();
                    this.$refs.searchList.reload();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.loadingSubEvents = false;
                }
            }
        },

        async createNewExpressSubEvent() {
            const event = new Event();
            await event.save();
            await this.addSubEvent(event);
            this.onEdit(event, true);
        },

        async createNewSubEvent() {
            const event = new Event();
            await event.save();
            await this.addSubEvent(event);
            this.onEdit(event, false);
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    display: grid;
    grid-template-areas: 'search assigned';
    grid-template-rows: auto;
    grid-template-columns: 1fr 1fr;
}
.search-panel {
    grid-area: search;
}
.assigned-panel {
    grid-area: assigned;
    background-color: var(--toolbar-bg-color);
}
</style>
